<div class="doc-content">
  <h1>Getting Started with Idea Financial Broker API</h1>

  <section class="doc-section">
    <h2>Overview</h2>
    <p>
      Idea Financial Universal Broker API manages broker application requests between the broker's database and Idea Financial database.
      This document outlines the high-level functionalities and the JSON structures used by the Web Services.
    </p>
    <p>
      Successful application submission requires sending application (Send application) and required attachments (Application file upload). Next API allows to accept an offer and upload required closing documents (stipulations).
      It also allows to update an application if it gets into an incomplete status. Finally, a contract can be sent to a customer if all requirements have been met and the contract is ready.
    </p>
    <ul class="disc-list">
      <li>The following table defines the inputs and outputs of each web service transaction.</li>
      <li>Next, business process diagram explains the workflow</li>
      <li>Sequence diagrams depict covered use cases</li>
    </ul>
    <p>
      <strong>Note:</strong>
    </p>
    <ul class="disc-list">
      <li>There’s a <strong>test only</strong> endpoint that allows self-service testing during the integration process,</li>
      <li><strong>Integrators working with multiple Partners</strong> can use aliases while creating users to circumvent the email address uniqueness requirement e.g. name+partner&#64;integrator.com.</li>
    </ul>

  </section>

  <section class="doc-section">
    <h2>Available Services</h2>
    <div class="table-responsive">
      <table class="service-table">
        <thead>
        <tr>
          <th>Service</th>
          <th>Description</th>
          <th>Output</th>
        </tr>
        </thead>
        <tbody>
        <tr>
          <td>Token</td>
          <td>This service will provide the token that will be used to access any of the Universal Broker API web services until the token is expired.</td>
          <td>This service will provide a token that will be part of the security and authentication protocol.</td>
        </tr>
        <tr>
          <td>Create Agent</td>
          <td>The endpoint allows for adding a new agent. The agent can receive notifications, and if a role is assigned to them, they also gain access to the Portal.</td>
          <td>This service will return created agent.</td>
        </tr>
        <tr>
          <td>Update Agent</td>
          <td>The endpoint allows for updating an selected agent. Using endpoint, it is possible to update single values of an object.</td>
          <td>This service will return updated agent.</td>
        </tr>
        <tr>
          <td>Get Agent Details</td>
          <td>The endpoint provides agents business data such as, status, email, role, name and phone number.</td>
          <td>This service will return the Agent details.</td>
        </tr>
        <tr>
          <td>Get List of Agents</td>
          <td>The endpoint provides a list of the agents' business data such as, statuses, emails, roles, names and phone numbers.</td>
          <td>This service will return the list of Agents' details.</td>
        </tr>
        <tr>
          <td>Create Application</td>
          <td>This service will allow brokers to create applications from their database into Idea Financial database.</td>
          <td>This service will provide a success message along with the application ID created.</td>
        </tr>
        <tr>
          <td>Update Application</td>
          <td>This service will allow brokers to update a selected application</td>
          <td>Returns success code</td>
        </tr>
        <tr>
          <td>Application File Upload</td>
          <td>
            <p>This service will allow brokers to send documents/files associated to applications they have submitted. This service accepts single file per request. The maximum file size is 20MB. Acceptable file formats are: .png, .jpeg, .jpg, .gif, .tiff, .tif, .pdf</p>
            <p>Limit of max. 6 files for bank statements and 4 files of application per submission in Draft status.</p>
            Limit of max. 24 files in status Submission Incomplete status
          </td>
          <td>This service will provide a success message along with the document ID(s) created.</td>
        </tr>
        <tr>
          <td>Push Application</td>
          <td>This service will allow brokers to push applications from current status to next</td>
          <td>Returns success code</td>
        </tr>
        <tr>
          <td>Application Pushback - <strong class="red">TESTING PURPOSES ONLY</strong></td>
          <td>The endpoint is available only in <strong>STAGE environment</strong> and allows Broker API users to test application flows by pushing a submission to a desired status without Idea Financial personnel involvement. Supported transitions:
            <ul class="disc-list">
              <li>From <strong>Processing → Submission Incomplete</strong> (populates Incomplete Info)</li>
              <li>From <strong>Processing → Approved</strong> (creates sample offers and requirements)</li>
              <li>From <strong>Processing → Declined</strong> (populates Decline Reason)</li>
              <li>From <strong>Closing → Closing Incomplete</strong> (populates Incomplete Info and updates requirements)</li>
              <li>From <strong>Closing → Contract Ready</strong> (approves all requirements).</li>
            </ul>
          </td>
          <td>Returns success code and update the submission status with corresponding test data.</td>
        </tr>
        <tr>
          <td>Get Application Details</td>
          <td>This service provides application status, offer information and checkout requirements (if applicable) related to a specific application id. <strong>Important notes about statuses:</strong>
            <ul class="disc-list">
              <li>Application status defines where it is in the process (see <a href="admin/api-docs#broker-portal-api-diagram">diagram</a>).</li>
              <li>Checkout requirements status defines whether they are approved, required or not required for selected offer. There’s common set of requirements for an application and some may be applicable for an offer while others not.</li>
              <li>Offer status defines which was accepted and therefore defines which requirements are applicable.</li>
              <!-- Word for word ApplicationDetailsResultDescriptions.Offers in the Broker API; change both together. -->
              <li>Offers can be returned before the application reaches the <i>offer</i> status. While the application <i>status</i> is <i>processing</i>, <i>draft</i>, <i>submission-incomplete</i> or <i>conditional-offer</i>, their values are provisional, can still change, and must not be presented to a merchant as a firm offer. Offer values are final only once the application has reached <i>offer</i>, except that accepting a term loan offer with <i>finalAmount</i> or <i>finalInterestRate</i> replaces its <i>amount</i> and <i>interestRate</i>.</li>
            </ul>
          </td>
          <td>This service will return the offer details along with the opportunity account manager information and checkout requirements</td>
        </tr>
        <tr>
          <td>Checkout Requirement File Upload</td>
          <td>
            <p>This service will allow brokers to upload and send files under checkout requirements (stipulations). This service accepts single file per request. The maximum file size is 20MB. Acceptable file formats are: .png, .jpeg, .jpg, .gif, .tiff, .tif, .pdf.</p>
            Limit of max. 25 files per requirement in Offer, Conditional Offer or Closing Incomplete status.
          </td>
          <td>This service will provide a success message along with the document ID(s) created.</td>
        </tr>
        <tr>
          <td>Offer Acceptance</td>
          <td>
            <p>This service will allow brokers to notify Idea Financial which offer was accepted by client.</p>
            <p>Offers can be accepted while the application status is <i>conditional-offer</i>. Their values stay provisional until the status is <i>offer</i>, and can still change after acceptance.</p>
            <p>Note that accepting Term Loan has optional parameters that may be used:</p>
            <ul class="disc-list">
              <li>if you get offer details where <i>interestRate < maxInterestRate</i> then you can submit <i>finalInterestRate</i> within that range</li>
              <li>if you get offer details where <i>minAmount < amount</i> then you can submit <i>finalAmount</i> within that range</li>
            </ul>
          </td>
          <td>Returns success code</td>
        </tr>
        <tr>
          <td>Mark application as not interested</td>
          <td>This service will allow brokers to notify Idea Financial that customer is no more interested in applying for financing.</td>
          <td>Returns success code</td>
        </tr>
        <tr>
          <td>Reactivate</td>
          <td>This service will allow brokers to reactivate application for financing.</td>
          <td>Returns success code</td>
        </tr>
        <tr>
          <td>Get Competitors</td>
          <td>This service will allow brokers to get list of competitors.</td>
          <td>Returns success code</td>
        </tr>
        <tr>
          <td>Get not interested reasons</td>
          <td>This service will allow brokers to get list of not interest reasons.</td>
          <td>Returns success code</td>
        </tr>
        </tbody>
      </table>
    </div>
    <div class="api-image-container" id="broker-portal-api-diagram">
      <img src="assets/api-docs/graphs/Broker_Portal_&_API_-_v.4.11_-_18_Aug_25.png" alt="API Flow Diagram" class="api-flow-image">
      <a href="assets/api-docs/graphs/Broker_Portal_&_API_-_v.4.11_-_18_Aug_25.png" target="_blank" rel="noopener noreferrer">Click to open in new tab</a>
    </div>
  </section>

  <section class="doc-section">
    <h2>Process Flows</h2>

    <div class="flow-diagram">
      <h3>1. Agents Management</h3>
      <div class="api-image-container">
        <img src="assets/api-docs/graphs/agent_management_graph.png" alt="Agents Management Flow" class="api-flow-image">
      </div>
    </div>

    <div class="flow-diagram">
      <h3>2. Happy Flow</h3>
      <div class="api-image-container">
        <img src="assets/api-docs/graphs/happy_path_flow_graph.png" alt="Happy Flow Diagram" class="api-flow-image">
      </div>
    </div>

    <div class="flow-diagram">
      <h3>3. Submission Incomplete</h3>
      <div class="api-image-container">
        <img src="assets/api-docs/graphs/submission_incomplete_graph.png" alt="Submission Incomplete Flow" class="api-flow-image">
      </div>
    </div>

    <div class="flow-diagram">
      <h3>4. Submission Declined</h3>
      <div class="api-image-container">
        <img src="assets/api-docs/graphs/submission_declined.png" alt="Submission Declined Flow" class="api-flow-image">
      </div>
    </div>

    <div class="flow-diagram">
      <h3>5. Customer Not Interested</h3>
      <div class="api-image-container">
        <img src="assets/api-docs/graphs/submission_customer_not_interested.png" alt="Customer Not Interested Flow" class="api-flow-image">
      </div>
    </div>

    <div class="flow-diagram">
      <h3>6. Closing Incomplete</h3>
      <div class="api-image-container">
        <img src="assets/api-docs/graphs/submission_closing_incomplete.png" alt="Closing Incomplete Flow" class="api-flow-image">
      </div>
    </div>
  </section>
</div>
